/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import type { DocNode } from '@atlaskit/adf-schema/doc';
import { getEmptyADF } from '@atlaskit/adf-utils/empty-adf';
import { JiraPlaybookAri } from '@atlassian/ari/jira/playbook';
import { usePathParam } from '@atlassian/react-resource-router';

import { getProjectSettingsPageUrl } from '@jira/servicedesk__navigation/src/getProjectSettingsPageUrl.tsx';
import type { PlaybooksTab } from '@jira/playbooks__common/src/PlaybooksTab.tsx';
import type { JiraPlaybookStepType } from './__generated__/PlaybookItemDetail.graphql.ts';
import type { StepData, Step, StepsDetail } from './FormValues.types.tsx';

export const getStepData = (
	id: string,
	name: string,
	type: JiraPlaybookStepType,
	index: number,
): StepData => {
	return { stepId: id, name, index, type };
};

export const isStep = (data: Record<string | symbol, unknown>): data is StepData => {
	if (data) {
		const { type } = data;
		return type === 'INSTRUCTIONAL_RULE' || type === 'AUTOMATION_RULE' || type === 'CHECKLIST';
	}
	return false;
};

export const getJiraPlaybookARI = (
	cloudId: string,
	activationId: string,
	playbookId: string,
): string => {
	return JiraPlaybookAri.create({
		siteId: cloudId,
		activationId,
		playbookId,
	}).toString();
};

export const getJiraPlaybookIdFromARI = (playbookAri: string): string => {
	return JiraPlaybookAri.parse(playbookAri).playbookId;
};

export const useReturnToListUrl = (): [string] => {
	const [projectKey = ''] = usePathParam('projectKey');

	const playbookListLink = getProjectSettingsPageUrl(projectKey, 'playbooks/list');
	return [playbookListLink];
};

export const usePlaybooksTabUrl = (tab: PlaybooksTab): string => {
	const [projectKey = ''] = usePathParam('projectKey');

	return getProjectSettingsPageUrl(projectKey, `playbooks/${tab}`);
};

export const getIsDescriptionEmpty = (description?: DocNode): boolean => {
	if (description && Object.keys(description).length > 0) {
		return JSON.stringify(description) === JSON.stringify(getEmptyADF());
	}
	return true;
};

export const getStepsOrder = (data: Step[]): string[] => {
	return data.map((step) => step.stepId);
};

export const getStepsDetail = (data: Step[]): StepsDetail => {
	const acc: StepsDetail = {};
	data.forEach((step) => {
		acc[step.stepId] = step;
	});
	return acc;
};

const PLAYBOOKS_TABS: Set<PlaybooksTab> = new Set(['list', 'logs', 'templates']);

export const isPlaybooksTab = (value: string | undefined): value is PlaybooksTab => {
	// oxlint-disable-next-line typescript/consistent-type-assertions
	return value !== undefined && PLAYBOOKS_TABS.has(value as PlaybooksTab);
};
